import queryString from 'query-string'
import withWidth from '@material-ui/core/withWidth'
import { ElementsConsumer, useStripe, useElements } from '@stripe/react-stripe-js'
import { loadFragments } from '../../store'
import Banner from '../../components/Banner'
import { andWhatNext } from '../../helpers'
import { FORMS } from '../../constants'
import useStyles from './styles'
import { setPaymentListener, buildPaymentRequest, getIntent } from './helpers'
import Intro from './Intro'
import DonateInputs from './DonateInputs'
import CardPayment from './CardPayment'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import StepContent from '@material-ui/core/StepContent'
import PaymentRequestButton from './PaymentRequestButton'
import Signup from './Signup'

import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const Donate = (props) => {
  const dispatch = useDispatch()
  const act = useAct()

  const classes = useStyles(props)
  const fragments = useSelector((state) => state.local.fragments)
  const parsed = queryString.parse(location.search)
  const formRefs = { amount: useRef() }
  const [formData, setFormData] = useState(FORMS.donation.fields)
  const [formErrors, setFormErrors] = useState(
    _.mapValues(FORMS.donation.fields, () => null)
  )
  const [step, setStep] = useState(0)
  const [donationMethod, setDonationMethod] = useState(null)
  const [paymentRequest, setPaymentRequest] = useState(null)
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(true)
  const [prbFailed, setPrbFailed] = useState(false)
  const stripe = useStripe()
  const [cookies] = useCookies()
  const [user, setUser] = useState()

  useEffect(() => {
    dispatch(loadFragments())
  }, [])

  useEffect(() => {
    setUser({
      ..._.pick(cookies['neu_nclb_client'], ['first_name', 'last_name', 'email']),
    })
  }, [cookies])

  const genericError = () => {
    toast.error('An unexpected error occurred - please reload the page and try and again')
  }

  const onConfirmAmount = async () => {
    setStep(1)
    act.donation.upsert({ amount: formData.amount })

    if (stripe) {
      const pr = prbFailed ? null : await buildPaymentRequest(formData.amount, stripe)

      if (pr) {
        setDonationMethod('paymentRequestButton')
        getIntent(
          pr,
          formData.amount,
          ({ data: { id, clientSecret } }) => {
            act.donation.upsert({ paymentIntentId: id })
            setPaymentListener({
              paymentRequest: pr,
              clientSecret,
              onPaymentSuccess,
              onPaymentFail: onPrbPaymentFail,
              stripe,
            })
            setPaymentRequest(pr)
          },
          genericError
        )
      } else {
        setDonationMethod('card')
      }
    } else {
      genericError()
    }
  }

  useEffect(() => {
    if (!!paymentRequest) {
      setPaymentMethodLoading(false)
    }
  }, [paymentRequest])

  const onPaymentSuccess = () => {
    finalStep()
  }

  const onPrbPaymentFail = () => {
    setPrbFailed(true)
    toast.error('Browser payment failed - please try entering your card details directly')
    setDonationMethod('card')
  }

  const finalStep = () => {
    setStep(2)
    act.donation.save().then(() => {
      setTimeout(
        () =>
          (window.location.href = andWhatNext('donate', { hasSigned: !!parsed.post })),
        2000
      )
    })

    toast.success('Next step coming up...', {
      style: { backgroundColor: '#019dc4' },
      autoClose: 3000,
    })
  }

  const gridParams = { item: true, xs: 12, container: true }
  return (
    <Fade in={!_.isEmpty(fragments)}>
      <Container maxWidth={false} disableGutters>
        <Banner
          title="LEFT BEHIND"
          titleExtra="NO CHILD"
          preTitle="take action to demand:"
          slug="donate_banner"
        />
        <ToastContainer />
        <Grid
          container
          className={classes.container}
          direction="column"
          alignItems="center"
        >
          <Intro classes={classes} post={parsed?.post} fragments={fragments} />
          <Grid {...gridParams} justify="center" className={classes.donateContainer}>
            <Grid item xs={12} sm={10} md={8} lg={5}>
              <Stepper
                activeStep={step}
                orientation="vertical"
                className={classes.stepper}
              >
                <Step>
                  <StepLabel
                    className={classes.stepLabel}
                    onClick={() => {
                      step < 2 && setStep(0)
                    }}
                  >
                    Select donation amount
                  </StepLabel>
                  <StepContent>
                    <DonateInputs
                      {...{
                        onSelectAmount: (field, evt) =>
                          setFormData({ amount: Number(evt.target.value) * 100 }),
                        onConfirmAmount,
                        onError: (err) => setFormErrors(err),
                        formRefs,
                        formErrors,
                        formData,
                        classes,
                      }}
                    />
                  </StepContent>
                </Step>
                <Step>
                  <StepLabel className={classes.stepLabel}>Payment</StepLabel>
                  <StepContent>
                    <Signup
                      user={user}
                      fragments={fragments}
                      onSubmit={(data) => setUser(data)}
                    />
                    {donationMethod === 'paymentRequestButton' && (
                      <PaymentRequestButton
                        {...{
                          amount: formData.amount,
                          paymentRequest,
                          loading: paymentMethodLoading,
                          prepaymentSignup: true,
                        }}
                      />
                    )}
                    {donationMethod === 'card' && (
                      <ElementsConsumer>
                        {({ elements, stripe }) => (
                          <CardPayment
                            elements={elements}
                            stripe={stripe}
                            formData={user}
                            prepaymentSignup
                            onSuccess={finalStep}
                            amount={formData.amount}
                            showError={toast.error}
                          />
                        )}
                      </ElementsConsumer>
                    )}
                  </StepContent>
                </Step>
                <Step>
                  <StepLabel className={classes.stepLabel}>Confirmation</StepLabel>
                  <StepContent>
                    <Typography variant="body1" style={{ marginBottom: 20 }}>
                      Thanks for your donation! Do you want to help support us further?
                      Then take action here:
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      href="/action"
                    >
                      Sign our petition
                    </Button>
                  </StepContent>
                </Step>
              </Stepper>
            </Grid>
          </Grid>
          )}
        </Grid>
      </Container>
    </Fade>
  )
}

export default withWidth()(Donate)
